import React, { useState } from "react";

const API_BASE = "https://api.malidag.com";

const DEFAULT_ROWS = [
  {
    type: "pair",          // for UI only
    symbol: "",
    tokenAddress: "",
    pairAddress: "",
    tokenName: "",
    decimals: "",
  },
];

export default function TokensListAdder() {
  const [authKey, setAuthKey] = useState("");
  const [chainId, setChainId] = useState(56);
  const [rows, setRows] = useState(DEFAULT_ROWS);

  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");

  const isHex = (a) => /^0x[a-f0-9]{40}$/.test(String(a || "").toLowerCase());

  const addRow = () =>
    setRows((r) => [
      ...r,
      { type: "pair", symbol: "", tokenAddress: "", pairAddress: "", tokenName: "", decimals: "" },
    ]);

  const removeRow = (idx) => setRows((prev) => prev.filter((_, i) => i !== idx));

  const updateRow = (i, field, value) => {
    setRows((prev) => {
      const copy = [...prev];
      copy[i] = { ...copy[i], [field]: value };
      return copy;
    });
  };

  // ✅ only include fields if user provided them
  const normalize = (r) => {
    const tokenAddress = String(r.tokenAddress || "").toLowerCase().trim();
    const pairAddress = String(r.pairAddress || "").toLowerCase().trim();

    const symbol = String(r.symbol || "").trim();
    const tokenName = String(r.tokenName || "").trim();
    const decimalsRaw = String(r.decimals ?? "").trim();

    const payload = {
      chainId: Number(chainId),

      ...(tokenAddress ? { tokenAddress } : {}),
      ...(pairAddress ? { pairAddress } : {}),

      ...(symbol ? { symbol: symbol.toUpperCase() } : {}),
      ...(tokenName ? { tokenName } : {}),
    };

    if (decimalsRaw !== "") {
      payload.decimalAddress = Number(decimalsRaw);
    }

    // keep dropdown behavior if you want
    // (backend can also infer it from pairAddress)
    if (r.type) payload.type = String(r.type).toLowerCase().trim();

    return payload;
  };

  const validateRows = (clean) => {
    if (!Number(chainId)) return "❌ Chain ID is required";
    if (!clean.length) return "❌ Add at least 1 row.";

    for (let i = 0; i < clean.length; i++) {
      const t = clean[i];

      // if you keep type dropdown validation:
      if (t.type && t.type !== "token" && t.type !== "pair") {
        return `❌ Row ${i + 1}: type must be token or pair`;
      }

      // if type=token then tokenAddress required
      if (t.type === "token") {
        if (!t.tokenAddress) return `❌ Row ${i + 1}: tokenAddress required`;
        if (!isHex(t.tokenAddress)) return `❌ Row ${i + 1}: invalid tokenAddress`;
      }

      // if type=pair then pairAddress required
      if (t.type === "pair") {
        if (!t.pairAddress) return `❌ Row ${i + 1}: pairAddress required`;
        if (!isHex(t.pairAddress)) return `❌ Row ${i + 1}: invalid pairAddress`;
        if (t.tokenAddress && !isHex(t.tokenAddress)) {
          return `❌ Row ${i + 1}: invalid tokenAddress`;
        }
      }

      // If user didn't choose a type (or you later remove dropdown),
      // we still require at least one address:
      if (!t.pairAddress && !t.tokenAddress) {
        return `❌ Row ${i + 1}: tokenAddress or pairAddress required`;
      }

      // decimals validation only if present
      if (t.decimalAddress != null) {
        const d = Number(t.decimalAddress);
        if (!Number.isFinite(d) || d < 0 || d > 30) {
          return `❌ Row ${i + 1}: decimals must be 0-30`;
        }
      }
    }

    return null;
  };

  const submit = async () => {
    setStatus("");
    setLoading(true);

    try {
      const clean = rows
        .filter((r) => r.pairAddress || r.tokenAddress)
        .map(normalize);

      const err = validateRows(clean);
      if (err) {
        setStatus(err);
        return;
      }

      let inserted = 0;
      let exists = 0;
      let failed = 0;
      const failMsgs = [];

      for (const payload of clean) {
        try {
          const res = await fetch(`${API_BASE}/api/tokens`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ authKey, ...payload }), // ✅ no x-api-key header
          });

          const data = await res.json().catch(() => ({}));

          if (!res.ok) {
            if (res.status === 409 || /exists/i.test(data?.error || "")) {
              exists += 1;
              continue;
            }
            failed += 1;
            if (failMsgs.length < 5) failMsgs.push(data?.error || "Unknown error");
            continue;
          }

          if (data?.inserted) inserted += 1;
          else exists += 1;
        } catch (e) {
          console.error(e);
          failed += 1;
          if (failMsgs.length < 5) failMsgs.push(e?.message || "Network error");
        }
      }

      setStatus(
        `✅ Done.\nInserted: ${inserted}\nExists: ${exists}\nFailed: ${failed}` +
          (failMsgs.length ? `\n\nErrors:\n- ${failMsgs.join("\n- ")}` : "")
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white shadow-lg rounded-2xl p-6 w-full max-w-2xl mt-6">
      <h2 className="text-xl font-semibold text-gray-700 mb-4 text-center">
        ➕ Add Tokens / Pairs to tokensList
      </h2>

      <div className="mb-3">
        <label className="block text-sm font-medium">Admin Key</label>
        <input
          type="password"
          className="w-full border rounded-md p-2"
          value={authKey}
          onChange={(e) => setAuthKey(e.target.value)}
          placeholder="Enter admin key"
        />
      </div>

      <div className="mb-4">
        <label className="block text-sm font-medium">Chain ID</label>
        <input
          type="number"
          className="w-full border rounded-md p-2"
          value={chainId}
          onChange={(e) => setChainId(Number(e.target.value))}
          placeholder="56"
        />
        <div className="text-xs text-gray-500 mt-1">
          Example: 56 = BSC, 42161 = Arbitrum
        </div>
      </div>

      <div className="border p-3 rounded-md bg-gray-50">
        <h3 className="font-medium text-gray-700 mb-2">Entries</h3>

        {rows.map((r, i) => (
          <div key={i} className="mb-3 p-2 rounded-md border bg-white">
            <div className="grid grid-cols-4 gap-2">
              <select
                className="border rounded-md p-2 col-span-1"
                value={r.type}
                onChange={(e) => updateRow(i, "type", e.target.value)}
              >
                <option value="pair">pair</option>
                <option value="token">token</option>
              </select>

              <input
                className="border rounded-md p-2 col-span-1"
                placeholder="Symbol (optional)"
                value={r.symbol}
                onChange={(e) => updateRow(i, "symbol", e.target.value)}
              />

              <input
                className="border rounded-md p-2 col-span-1"
                type="number"
                placeholder="Dec (optional)"
                value={r.decimals}
                onChange={(e) => updateRow(i, "decimals", e.target.value)}
              />

              <button
                type="button"
                className="border rounded-md p-2 col-span-1 text-red-600 hover:bg-red-50"
                onClick={() => removeRow(i)}
                disabled={rows.length === 1}
              >
                Remove
              </button>

              <input
                className="border rounded-md p-2 col-span-4"
                placeholder="Token Address (0x...) - required for type=token, optional for pair"
                value={r.tokenAddress}
                onChange={(e) => updateRow(i, "tokenAddress", e.target.value)}
              />

              <input
                className="border rounded-md p-2 col-span-4"
                placeholder="Pair Address (0x...) - required for type=pair"
                value={r.pairAddress}
                onChange={(e) => updateRow(i, "pairAddress", e.target.value)}
              />

              <input
                className="border rounded-md p-2 col-span-4"
                placeholder="Token Name (optional)"
                value={r.tokenName}
                onChange={(e) => updateRow(i, "tokenName", e.target.value)}
              />
            </div>

            <div className="text-xs text-gray-500 mt-2">
              {r.type === "pair"
                ? "pair: requires Pair Address. Token Address optional."
                : "token: requires Token Address."}
            </div>
          </div>
        ))}

        <button type="button" onClick={addRow} className="text-blue-600 text-sm hover:underline">
          + Add Another
        </button>
      </div>

      <button
        type="button"
        onClick={submit}
        disabled={loading}
        className="w-full bg-blue-600 text-white py-2 rounded-md hover:bg-blue-700 transition mt-4"
      >
        {loading ? "Adding..." : "Add to tokensList"}
      </button>

      {status && (
        <p
          className={`text-center mt-3 ${
            status.includes("✅") ? "text-green-600" : "text-red-500"
          }`}
          style={{ whiteSpace: "pre-line" }}
        >
          {status}
        </p>
      )}
    </div>
  );
}
